// FEEL parser — precedence-climbing Pratt-style, produces a minimal AST.
//
// Grammar (subset):
//   expr        = if_expr | for_expr | quantified | disjunction
//   if_expr     = 'if' expr 'then' expr 'else' expr
//   for_expr    = 'for' IDENT 'in' expr 'return' expr
//   quantified  = ('some'|'every') IDENT 'in' expr 'satisfies' expr
//   disjunction = conjunction ('or' conjunction)*
//   conjunction = comparison ('and' comparison)*
//   comparison  = arith [cmp_op arith]       | arith 'between' arith 'and' arith
//                | arith 'in' (arith | '(' positiveUnaryTests ')')
//   arith       = term (('+'|'-') term)*
//   term        = pow (('*'|'/') pow)*
//   pow         = unary ['**' unary]
//   unary       = ('-'|'not') unary | postfix
//   postfix     = primary ( '.' IDENT | '(' args ')' | '[' expr ']' )*
//   primary     = NUMBER | STRING | BOOLEAN | NULL | IDENT
//                | '(' expr ')' | list | context
//   list        = '[' [expr (',' expr)*] ']'
//   context     = '{' [entry (',' entry)*] '}'
//   entry       = (IDENT | STRING) ':' expr

import { tokenize, T } from './Lexer';

export class FeelParseError extends Error {
  constructor(msg, token) {
    super(`[feel/parse] ${msg}${token ? ` at pos ${token.start}` : ''}`);
    this.token = token;
  }
}

class Parser {
  constructor(tokens) {
    this.tokens = tokens;
    this.i = 0;
  }
  peek(off = 0) { return this.tokens[this.i + off]; }
  next() { return this.tokens[this.i++]; }
  eof() { return this.peek().type === T.EOF; }

  match(type, value) {
    const tk = this.peek();
    if (tk.type !== type) return false;
    if (value != null) {
      if (Array.isArray(value)) return value.includes(tk.value);
      return tk.value === value;
    }
    return true;
  }

  eat(type, value) {
    if (this.match(type, value)) return this.next();
    return null;
  }

  expect(type, value, label) {
    const tk = this.eat(type, value);
    if (!tk) {
      const got = this.peek();
      throw new FeelParseError(
        `expected ${label || `${type}${value ? `('${value}')` : ''}`}, got ${got.type}('${got.value}')`,
        got,
      );
    }
    return tk;
  }

  parseExpression() {
    const e = this.expr();
    if (!this.eof()) {
      throw new FeelParseError(`unexpected trailing token '${this.peek().value}'`, this.peek());
    }
    return e;
  }

  expr() {
    if (this.match(T.KEYWORD, 'if')) return this.ifExpr();
    if (this.match(T.KEYWORD, 'for')) return this.forExpr();
    if (this.match(T.KEYWORD, 'some') || this.match(T.KEYWORD, 'every')) return this.quantExpr();
    return this.disjunction();
  }

  ifExpr() {
    this.expect(T.KEYWORD, 'if');
    const cond = this.expr();
    this.expect(T.KEYWORD, 'then');
    const thenBranch = this.expr();
    this.expect(T.KEYWORD, 'else');
    const elseBranch = this.expr();
    return { type: 'If', cond, then: thenBranch, else: elseBranch };
  }

  forExpr() {
    this.expect(T.KEYWORD, 'for');
    const name = this.expect(T.IDENT, null, 'iteration variable');
    this.expect(T.KEYWORD, 'in');
    const iter = this.expr();
    this.expect(T.KEYWORD, 'return');
    const body = this.expr();
    return { type: 'For', name: name.value, iter, body };
  }

  quantExpr() {
    const kw = this.next().value; // 'some' | 'every'
    const name = this.expect(T.IDENT, null, 'iteration variable');
    this.expect(T.KEYWORD, 'in');
    const iter = this.expr();
    this.expect(T.KEYWORD, 'satisfies');
    const body = this.expr();
    return { type: 'Quantified', quantifier: kw, name: name.value, iter, body };
  }

  disjunction() {
    let left = this.conjunction();
    while (this.match(T.KEYWORD, 'or')) {
      this.next();
      const right = this.conjunction();
      left = { type: 'Logical', op: 'or', left, right };
    }
    return left;
  }

  conjunction() {
    let left = this.comparison();
    while (this.match(T.KEYWORD, 'and')) {
      this.next();
      const right = this.comparison();
      left = { type: 'Logical', op: 'and', left, right };
    }
    return left;
  }

  comparison() {
    const left = this.arith();
    if (this.match(T.OP, ['=', '!=', '<', '<=', '>', '>='])) {
      const op = this.next().value;
      const right = this.arith();
      return { type: 'Compare', op, left, right };
    }
    if (this.match(T.KEYWORD, 'between')) {
      this.next();
      const low = this.arith();
      this.expect(T.KEYWORD, 'and');
      const high = this.arith();
      return { type: 'Between', value: left, low, high };
    }
    if (this.match(T.KEYWORD, 'in')) {
      this.next();
      // Allow `in (<unary tests>)` or `in <expr>` (which is treated as list-member)
      if (this.match(T.PUNCT, '(')) {
        this.next();
        const tests = [];
        tests.push(this.arith());
        while (this.eat(T.PUNCT, ',')) tests.push(this.arith());
        this.expect(T.PUNCT, ')');
        return { type: 'In', value: left, tests };
      }
      const rhs = this.arith();
      return { type: 'In', value: left, tests: [rhs] };
    }
    return left;
  }

  arith() {
    let left = this.term();
    while (this.match(T.OP, ['+', '-'])) {
      const op = this.next().value;
      const right = this.term();
      left = { type: 'Binary', op, left, right };
    }
    return left;
  }

  term() {
    let left = this.pow();
    while (this.match(T.OP, ['*', '/'])) {
      const op = this.next().value;
      const right = this.pow();
      left = { type: 'Binary', op, left, right };
    }
    return left;
  }

  pow() {
    const left = this.unary();
    if (this.match(T.OP, '**')) {
      this.next();
      const right = this.unary(); // right-associative
      return { type: 'Binary', op: '**', left, right };
    }
    return left;
  }

  unary() {
    if (this.match(T.OP, '-')) { this.next(); return { type: 'Unary', op: '-', arg: this.unary() }; }
    if (this.match(T.KEYWORD, 'not')) { this.next(); return { type: 'Unary', op: 'not', arg: this.unary() }; }
    return this.postfix();
  }

  postfix() {
    let node = this.primary();
    while (true) {
      if (this.eat(T.PUNCT, '.')) {
        const name = this.expect(T.IDENT, null, 'property name').value;
        node = { type: 'Path', obj: node, name };
      } else if (this.eat(T.PUNCT, '(')) {
        const args = [];
        const named = {};
        let isNamed = false;
        if (!this.match(T.PUNCT, ')')) {
          while (true) {
            // Named arg: IDENT ':' expr
            if (this.peek().type === T.IDENT && this.peek(1) && this.peek(1).type === T.PUNCT && this.peek(1).value === ':') {
              const n = this.next().value;
              this.next(); // ':'
              named[n] = this.expr();
              isNamed = true;
            } else {
              args.push(this.expr());
            }
            if (!this.eat(T.PUNCT, ',')) break;
          }
        }
        this.expect(T.PUNCT, ')');
        node = { type: 'Call', callee: node, args, named, isNamed };
      } else if (this.eat(T.PUNCT, '[')) {
        const index = this.expr();
        this.expect(T.PUNCT, ']');
        node = { type: 'Index', obj: node, index };
      } else {
        break;
      }
    }
    return node;
  }

  primary() {
    const tk = this.peek();

    if (tk.type === T.NUMBER)  { this.next(); return { type: 'Literal', value: tk.value, dataType: 'number' }; }
    if (tk.type === T.STRING)  { this.next(); return { type: 'Literal', value: tk.value, dataType: 'string' }; }
    if (tk.type === T.BOOLEAN) { this.next(); return { type: 'Literal', value: tk.value, dataType: 'boolean' }; }
    if (tk.type === T.NULL)    { this.next(); return { type: 'Literal', value: null, dataType: 'null' }; }
    if (tk.type === T.IDENT)   { this.next(); return { type: 'Ident', name: tk.value }; }

    if (this.eat(T.PUNCT, '(')) {
      const e = this.expr();
      this.expect(T.PUNCT, ')');
      return e;
    }

    if (this.eat(T.PUNCT, '[')) {
      const items = [];
      if (!this.match(T.PUNCT, ']')) {
        items.push(this.expr());
        while (this.eat(T.PUNCT, ',')) items.push(this.expr());
      }
      this.expect(T.PUNCT, ']');
      return { type: 'List', items };
    }

    if (this.eat(T.PUNCT, '{')) {
      const entries = [];
      if (!this.match(T.PUNCT, '}')) {
        entries.push(this.entry());
        while (this.eat(T.PUNCT, ',')) entries.push(this.entry());
      }
      this.expect(T.PUNCT, '}');
      return { type: 'Context', entries };
    }

    throw new FeelParseError(`unexpected token '${tk.value}'`, tk);
  }

  entry() {
    let key;
    if (this.peek().type === T.STRING) key = this.next().value;
    else if (this.peek().type === T.IDENT) key = this.next().value;
    else throw new FeelParseError(`expected entry key, got ${this.peek().type}`, this.peek());
    this.expect(T.PUNCT, ':');
    const value = this.expr();
    return { key, value };
  }
}

export function parse(source) {
  const tokens = tokenize(source);
  const p = new Parser(tokens);
  return p.parseExpression();
}

export { Parser };
